import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, Badge, PageLoad, ErrorMsg } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import CampusLoginAdmin from '../components/CampusLoginAdmin';

export default function SettingsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState(null);

  const handleClearEmailLog = async () => {
    setClearing(true);
    try {
      const res = await api.delete('/settings/email-log');
      setClearResult(`Done — ${res.data.deleted} log entries cleared. Students can now receive emails fresh.`);
      setConfirmClear(false);
    } catch (err) {
      setClearResult('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setClearing(false);
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, configRes, programsRes] = await Promise.all([
        api.get('/users'),
        api.get('/settings/config'),
        api.get('/programs'),
      ]);
      setUsers(usersRes.data);
      setConfig(configRes.data);
      setPrograms(programsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <PageLoad />;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <div className="page-sub">System configuration and user management</div>
        </div>
      </div>

      <div className="col gap-20">
        <Card title="Integration Status" sub="External service connections">
          <div className="integration-grid">
            <IntegCard
              label="Integration Mode"
              val={config?.integrationMode?.toUpperCase() || '—'}
              sub={config?.integrationMode === 'live' ? 'Emails and docs are live' : 'Emails suppressed'}
              badge={config?.integrationMode === 'live' ? 'live' : 'muted'}
              icon={<GridIcon />}
            />
            <IntegCard
              label="SMTP / Nodemailer"
              val={config?.smtpConfigured ? 'Connected' : 'Not Connected'}
              sub={config?.smtpFromEmail || 'No sending address set'}
              badge={config?.smtpConfigured ? 'live' : 'danger'}
              icon={<MailIcon />}
            />
            <IntegCard
              label="PandaDoc"
              val={config?.pandadocConfigured ? 'Connected' : 'Not Connected'}
              sub="Site agreement dispatch"
              badge={config?.pandadocConfigured ? 'live' : 'muted'}
              icon={<DocIcon />}
            />
            <IntegCard
              label="Sending Email"
              val={config?.smtpFromEmail || 'Not set'}
              sub="From address for all emails"
              badge={config?.smtpFromEmail ? 'connected' : 'muted'}
              icon={<AtIcon />}
            />
          </div>
        </Card>

        {user?.role === 'admin' && <CampusLoginAdmin />}

        <Card
          title="User Management"
          action={
            <button className="btn btn-primary btn-sm" onClick={() => setShowUserForm(!showUserForm)}>
              + Add User
            </button>
          }
          padded={false}
        >
          {showUserForm && (
            <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--line-2)' }}>
              <UserForm onSave={() => { setShowUserForm(false); fetchData(); }} onCancel={() => setShowUserForm(false)} />
            </div>
          )}
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <UserRow key={u.id} user={u} onUpdate={fetchData} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Programs" sub="Read-only reference" padded={false}>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th><th>Name</th><th>Hours Required</th><th>Required Documents</th>
                </tr>
              </thead>
              <tbody>
                {programs.map(p => (
                  <tr key={p.id}>
                    <td><span style={{ fontWeight: 700 }}>{p.code}</span></td>
                    <td className="muted">{p.name}</td>
                    <td className="num" style={{ fontWeight: 600 }}>{p.hours_required} hrs</td>
                    <td className="muted">
                      {['BMT', 'AMT', 'MOA'].includes(p.code)
                        ? 'First Aid, PIC, Immunization, Resume'
                        : 'Resume only'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Danger Zone" sub="Irreversible admin actions — use with caution">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              <strong>Reset email log</strong> — clears all sent-email records for students so every automated email can fire again fresh. Use this if emails were sent by mistake during an import.
            </div>
            {clearResult && (
              <div style={{ fontSize: 13, padding: '8px 12px', borderRadius: 8, background: 'var(--bg-base)', border: '1px solid var(--line)', color: 'var(--ink-1)' }}>
                {clearResult}
              </div>
            )}
            {!confirmClear ? (
              <button className="btn btn-danger btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setConfirmClear(true)}>
                Reset email log
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--status-danger-fg)' }}>This cannot be undone. Are you sure?</span>
                <button className="btn btn-danger btn-sm" onClick={handleClearEmailLog} disabled={clearing}>
                  {clearing ? 'Clearing…' : 'Yes, clear it'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmClear(false)}>Cancel</button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function IntegCard({ label, val, sub, badge, icon }) {
  const badgeLabel = badge === 'live' ? 'Active' : badge === 'connected' ? 'Connected' : badge === 'danger' ? 'Off' : 'N/A';
  return (
    <div className="integration">
      <div className="integ-top">
        <div className="ic-circle">{icon}</div>
        <span className={`badge ${badge} integ-badge`}>{badgeLabel}</span>
      </div>
      <div className="integ-body">
        <div className="label">{label}</div>
        <div className="val">{val}</div>
        <div className="sub">{sub}</div>
      </div>
    </div>
  );
}

function UserForm({ onSave, onCancel }) {
  const [form, setForm] = useState({ email: '', password: '', full_name: '', role: 'pc' });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form-grid-4" style={{ gap: 10 }}>
      {error && <ErrorMsg style={{ gridColumn: '1 / -1' }}>{error}</ErrorMsg>}
      <input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
        placeholder="Full Name" required className="input" />
      <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
        placeholder="Email" required className="input" />
      <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
        placeholder="Password" required className="input" />
      <div className="row gap-8">
        <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="select" style={{ flex: 1 }}>
          <option value="pc">PC</option>
          <option value="admin">Admin</option>
        </select>
        <button type="submit" className="btn btn-primary btn-sm">Add</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function UserRow({ user, onUpdate }) {
  const { user: currentUser, impersonate, impersonating } = useAuth();
  const [resetting, setResetting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmDeact, setConfirmDeact] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [impersonating_, setImpersonating_] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const [actionError, setActionError] = useState('');

  const handleImpersonate = async () => {
    setImpersonating_(true);
    try {
      await impersonate(user.id);
      window.location.href = '/';
    } catch (err) {
      console.error(err);
      setImpersonating_(false);
    }
  };

  const handleDeactivate = async () => {
    if (!confirmDeact) { setConfirmDeact(true); return; }
    setConfirmDeact(false);
    try {
      await api.patch(`/users/${user.id}/deactivate`);
      onUpdate();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setConfirmDelete(false);
    try {
      await api.delete(`/users/${user.id}`);
      onUpdate();
    } catch (err) {
      console.error(err);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) return;
    try {
      await api.put(`/users/${user.id}`, { ...user, password: newPassword });
      setResetting(false);
      setNewPassword('');
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleHostFinder = async () => {
    try {
      await api.put(`/users/${user.id}`, {
        ...user,
        is_host_specialist: !user.is_host_specialist,
      });
      onUpdate();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRoleChange = async () => {
    const nextRole = user.role === 'admin' ? 'pc' : 'admin';
    setChangingRole(true);
    setActionError('');
    try {
      await api.put(`/users/${user.id}`, { ...user, role: nextRole });
      onUpdate();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not update role');
    } finally {
      setChangingRole(false);
    }
  };

  return (
    <tr>
      <td className="nm">{user.full_name}</td>
      <td className="muted">{user.email}</td>
      <td>
        <span className={`badge ${user.role === 'admin' ? 'active' : 'muted'}`}>{user.role}</span>
        {user.is_host_specialist && (
          <span className="badge live" style={{ marginLeft: 6 }}>Host Finder</span>
        )}
      </td>
      <td>
        <span className={`badge ${user.is_active ? 'live' : 'danger'}`}>
          {user.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td>
        <div className="row gap-8">
          {resetting ? (
            <>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="New password"
                className="input"
                style={{ width: 140, fontSize: 12, padding: '4px 8px' }}
              />
              <button className="btn btn-coral btn-xs" onClick={handleResetPassword}>Save</button>
              <button className="btn btn-ghost btn-xs" onClick={() => { setResetting(false); setNewPassword(''); }}>Cancel</button>
            </>
          ) : confirmDelete ? (
            <>
              <span style={{ fontSize: 12, color: 'var(--status-danger-fg)' }}>Permanently delete?</span>
              <button className="btn btn-danger btn-xs" onClick={handleDelete}>Yes, delete</button>
              <button className="btn btn-ghost btn-xs" onClick={() => setConfirmDelete(false)}>Cancel</button>
            </>
          ) : (
            <>
              <button className="row-action" onClick={() => setResetting(true)} style={{ fontSize: 12.5 }}>Reset Password</button>
              {user.is_active && String(user.id) !== String(currentUser?.id) && (
                <button
                  className="btn btn-ghost btn-xs"
                  style={{ color: 'var(--accent)', fontWeight: 600 }}
                  onClick={handleRoleChange}
                  disabled={changingRole}
                >
                  {changingRole ? 'Updating…' : user.role === 'admin' ? 'Make PC' : 'Make Admin'}
                </button>
              )}
              {user.is_active && (
                <button
                  className="btn btn-ghost btn-xs"
                  style={{ color: user.is_host_specialist ? 'var(--status-danger-fg)' : 'var(--accent)', fontWeight: 600 }}
                  onClick={handleToggleHostFinder}
                >
                  {user.is_host_specialist ? 'Remove Host Finder' : 'Grant Host Finder'}
                </button>
              )}
              {currentUser?.role === 'admin' && !impersonating && user.is_active && String(user.id) !== String(currentUser.id) && (
                <button
                  className="btn btn-ghost btn-xs"
                  style={{ color: 'var(--accent)', fontWeight: 600 }}
                  onClick={handleImpersonate}
                  disabled={impersonating_}
                >
                  {impersonating_ ? 'Switching…' : 'Login as'}
                </button>
              )}
              {user.is_active && !confirmDeact && (
                <button className="btn btn-ghost btn-xs" style={{ color: 'var(--status-danger-fg)' }} onClick={() => setConfirmDeact(true)}>
                  Deactivate
                </button>
              )}
              {user.is_active && confirmDeact && (
                <>
                  <button className="btn-danger-ghost btn btn-xs" onClick={handleDeactivate}>Confirm?</button>
                  <button className="btn btn-ghost btn-xs" onClick={() => setConfirmDeact(false)}>Cancel</button>
                </>
              )}
              {!user.is_active && (
                <button className="btn btn-danger btn-xs" onClick={() => setConfirmDelete(true)}>
                  Delete
                </button>
              )}
              {actionError && (
                <span style={{ fontSize: 12, color: 'var(--status-danger-fg)' }}>{actionError}</span>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function GridIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>; }
function MailIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>; }
function DocIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>; }
function AtIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"/></svg>; }
